import type { Me, PlayerPreferences } from '@cribbage/protocol';
import { flight } from '~/session/singleFlight.js';
import { devicePlayerId, useAuthStore } from '~/store/authStore.js';
import { CARD_BACKS } from '~/store/cardBacks.js';
import { CARD_FRONTS } from '~/store/cardFronts.js';
import {
  BOT_SPEEDS,
  CROSS_POSITIONS,
  DECK_STYLES,
  type PendingPreferences,
  type SettingsState,
  useSettingsStore,
} from '~/store/settingsStore.js';
import { ARCH_FINISHES } from './boardFinishes.js';
import { entitlementsOf, paidStyleKeys } from './membership.js';

/**
 * The Game settings follow the player to every device they sign in on.
 *
 * Mitch, 2026-10-01: preferences sync to the account. The Game group syncs:
 * cards, board, play and the Cross playing position. Look and sound stays on
 * the device, because volume, haptics, contrast and motion answer to the room
 * and the screen rather than to the person. Every player has a server record, a
 * guest included, so a guest's settings are kept the same way.
 *
 * The rule survives a lost network:
 *
 * - A change on this device marks that setting as pending and is sent a moment
 *   later. Pending survives a reload, so a change made offline goes up on the
 *   next launch.
 * - Only pending settings are sent, and the server changes only the settings a
 *   request carries. So two devices editing different settings both land.
 * - When `/me` arrives, the server's copy is written over every setting that is
 *   not pending here. A pending one keeps the device's value and is sent again,
 *   because the server's copy in that answer is older than the change. A server
 *   with no copy yet is given all of this device's settings.
 *
 * See `docs/dev/client_settings.md`.
 */

/** The synced settings, by the store's key and the wire's. */
const SYNCED = {
  deck: 'deck',
  cardBack: 'card_back',
  cardFront: 'card_front',
  boardFinish: 'board_finish',
  confirmPlay: 'confirm_play',
  autoPlayForced: 'auto_play_forced',
  botSpeed: 'bot_speed',
  crossPosition: 'cross_position',
} as const satisfies Partial<Record<keyof SettingsState, keyof PlayerPreferences>>;

type SyncedKey = keyof typeof SYNCED;
type WireKey = (typeof SYNCED)[SyncedKey];
type Synced = Pick<SettingsState, SyncedKey>;

const SYNCED_KEYS = Object.keys(SYNCED) as SyncedKey[];
const WIRE_KEYS: readonly WireKey[] = SYNCED_KEYS.map((key) => SYNCED[key]);

/** Long enough that flicking through card backs sends one request, not twenty. */
export const SEND_DELAY_MS = 800;

/** The device's synced settings as the wire spells them. Every key, so a missed one fails to compile. */
export function toWire(settings: Synced): Required<PlayerPreferences> {
  return {
    deck: settings.deck,
    card_back: settings.cardBack,
    card_front: settings.cardFront,
    board_finish: settings.boardFinish,
    confirm_play: settings.confirmPlay,
    auto_play_forced: settings.autoPlayForced,
    bot_speed: settings.botSpeed,
    cross_position: settings.crossPosition,
  };
}

/**
 * The server's settings this build can use.
 *
 * A key the server has no value for is left out, and so is a style id this
 * build does not draw: a card back added in a newer release is not a reason
 * to throw away the one the player has here.
 */
export function fromWire(preferences: PlayerPreferences): Partial<Synced> {
  const known = <T>(list: readonly T[], value: unknown): T | undefined =>
    list.find((entry) => entry === value);
  const out: Partial<Synced> = {};
  const deck = known(DECK_STYLES, preferences.deck);
  if (deck !== undefined) out.deck = deck;
  const cardBack = CARD_BACKS.find((back) => back.id === preferences.card_back)?.id;
  if (cardBack !== undefined) out.cardBack = cardBack;
  const cardFront = known(CARD_FRONTS, preferences.card_front);
  if (cardFront !== undefined) out.cardFront = cardFront;
  const boardFinish = known(ARCH_FINISHES, preferences.board_finish);
  if (boardFinish !== undefined) out.boardFinish = boardFinish;
  if (typeof preferences.confirm_play === 'boolean') out.confirmPlay = preferences.confirm_play;
  if (typeof preferences.auto_play_forced === 'boolean') {
    out.autoPlayForced = preferences.auto_play_forced;
  }
  const botSpeed = known(BOT_SPEEDS, preferences.bot_speed);
  if (botSpeed !== undefined) out.botSpeed = botSpeed;
  const crossPosition = known(CROSS_POSITIONS, preferences.cross_position);
  if (crossPosition !== undefined) out.crossPosition = crossPosition;
  return out;
}

/** Only the named settings, as the wire spells them. */
function pick(preferences: PlayerPreferences, keys: readonly string[]): PlayerPreferences {
  return Object.fromEntries(Object.entries(preferences).filter(([key]) => keys.includes(key)));
}

function pendingNow(): PendingPreferences | null {
  return useSettingsStore.getState().preferencesPending;
}

function setPending(pending: PendingPreferences | null): void {
  useSettingsStore.getState().set('preferencesPending', pending);
}

/** Adds settings to what this player has pending. Another player's marks are not ours. */
function markPending(player: string, keys: readonly string[]): void {
  const current = pendingNow();
  const kept = current?.player === player ? current.keys : [];
  setPending({ player, keys: [...new Set([...kept, ...keys])] });
}

/**
 * Start keeping the Game settings and the server in step. Returns the stop.
 *
 * Called once from the app root. Sends happen after a change, when `/me`
 * arrives, and when the app comes back to the foreground with a change still
 * pending. There is no retry timer: a send that fails waits for the next of
 * those, so a device with no network makes no requests.
 */
export function startPreferenceSync(): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // Set while the server's copy is written to the store, so that write is not
  // mistaken for the player changing something.
  let applying = false;
  // One send at a time. Each reads the store when it starts, so a change made
  // while another is out is carried by the one queued behind it.
  const sends = flight();

  const send = async (): Promise<void> => {
    if (stopped) return;
    const player = useAuthStore.getState().user?.id;
    const pending = pendingNow();
    // Before `/me` answers there is nobody confirmed to send for. Its arrival
    // runs `reconcile`, which sends.
    if (player === undefined || pending?.player !== player || pending.keys.length === 0) return;
    // A paid style on a device whose player is not a Game Surface member stays
    // here unsent: it may predate the members-only gate, or belong to another
    // account. The server refuses a whole save that moves onto one, which would
    // take every other setting down with it. It stays pending, and goes once
    // `/me` shows the membership.
    const locked = entitlementsOf(useAuthStore.getState().user).surface
      ? []
      : paidStyleKeys(useSettingsStore.getState());
    const keys = pending.keys.filter((key) => !locked.includes(key));
    if (keys.length === 0) return;
    const settings = useSettingsStore.getState();
    const sent = pick(toWire(settings), keys);
    // Read now, while `sent` and the store agree: the player may pick another
    // style while the save is out.
    const paidSent = paidStyleKeys(settings).some((key) => key in sent);
    const outcome = await useAuthStore.getState().savePreferences(sent);
    // Rechecked after the wait: the player may have signed out, or changed
    // something while this was out.
    if (stopped || pendingNow()?.player !== player || outcome === 'failed') return;
    if (outcome === 'refused' && paidSent) {
      // A paid style sent while a cached `/me` still said member is refused
      // once the membership has lapsed, and the rest of the save with it. Ask
      // `/me` again: when it no longer shows Game Surface, the next send leaves
      // the paid style out and the other settings land.
      const answer = await useAuthStore.getState().refresh();
      if (stopped || pendingNow()?.player !== player) return;
      // This refresh brought no answer of its own: nothing is known yet, so
      // the change stays pending for the next send, `/me` or return to the app.
      // Its own answer decides, not the store, which a later `/me` may hold.
      if (answer === null) return;
      if (!entitlementsOf(answer).surface) {
        schedule(0);
        return;
      }
    }
    // Acknowledged, or refused for good: a refusal will not change on a second
    // try, and the next `/me` brings the server's copy. Either way a setting
    // changed again while this was out stays pending.
    const after = pendingNow();
    if (after === null) return;
    const now = toWire(useSettingsStore.getState());
    const left = after.keys.filter(
      (key) => !(key in sent) || now[key as WireKey] !== sent[key as WireKey],
    );
    setPending(left.length === 0 ? null : { player, keys: left });
  };

  const schedule = (delay = SEND_DELAY_MS): void => {
    if (stopped) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void sends.after(send);
    }, delay);
  };

  const reconcile = (user: Me): void => {
    // A server from before preferences existed says nothing either way.
    const server = user.preferences as PlayerPreferences | undefined;
    if (server === undefined) return;
    let pending = pendingNow();
    if (pending !== null && pending.player !== user.id) {
      // A change pending for another player is that player's, and this device
      // no longer holds a session to send it with.
      setPending(null);
      pending = null;
    }
    if (Object.keys(server).length === 0) {
      // Never saved from anywhere: this device's settings become the record,
      // all of them, even when one change is already pending (an install that
      // upgraded and changed something before its first answer).
      setPending({ player: user.id, keys: WIRE_KEYS });
      schedule(0);
      return;
    }
    const held = pending?.keys ?? [];
    const current = useSettingsStore.getState();
    const changes = Object.fromEntries(
      Object.entries(fromWire(server)).filter(
        ([key, value]) =>
          !held.includes(SYNCED[key as SyncedKey]) && current[key as SyncedKey] !== value,
      ),
    ) as Partial<Synced>;
    if (Object.keys(changes).length > 0) {
      applying = true;
      try {
        useSettingsStore.setState(changes);
      } finally {
        applying = false;
      }
    }
    if (held.length > 0) schedule(0);
  };

  const stopSettings = useSettingsStore.subscribe((state, previous) => {
    if (applying) return;
    const changed = SYNCED_KEYS.filter((key) => state[key] !== previous[key]);
    if (changed.length === 0) return;
    // The stored player answers before `/me` does, so a change made on an
    // offline launch is still marked for the right person.
    const player = useAuthStore.getState().user?.id ?? devicePlayerId();
    if (player === null) return;
    markPending(
      player,
      changed.map((key) => SYNCED[key]),
    );
    schedule();
  });

  const stopAuth = useAuthStore.subscribe((state, previous) => {
    if (state.user !== null && state.user !== previous.user) reconcile(state.user);
  });

  const resume = (): void => {
    const pending = pendingNow();
    if (document.visibilityState !== 'visible' || pending === null) return;
    const { token, user } = useAuthStore.getState();
    if (user !== null) {
      if (pending.player === user.id) schedule(0);
      return;
    }
    // A launch with no network kept its token and has no `/me` yet. Asking
    // again now is what brings `reconcile`, which sends the pending change.
    if (token !== null) void useAuthStore.getState().refresh();
  };
  document.addEventListener('visibilitychange', resume);

  const user = useAuthStore.getState().user;
  if (user !== null) reconcile(user);

  return () => {
    stopped = true;
    if (timer !== null) clearTimeout(timer);
    stopSettings();
    stopAuth();
    document.removeEventListener('visibilitychange', resume);
  };
}
